import React from 'react';
import { CalendarCheck, MagnifyingGlass } from '@phosphor-icons/react';
import { DocumentHead } from '@/components/DocumentHead';
import { Button } from '@/components/Button/Button';
import { ActionableErrorState } from '@/components/errors/ActionableErrorState';
import { routes } from '@/routes';
import { formatScheduledTime } from '@/utils/dateUtils';

type QuizUnavailablePageProps = {
  kind: 'not_found' | 'not_live' | 'load_error';
  title?: string;
  scheduledAt?: string | null;
  reloadPage?: () => void;
};

export default function QuizUnavailablePage({
  kind,
  title = 'Quiz',
  scheduledAt,
  reloadPage,
}: QuizUnavailablePageProps) {
  const isNotLive = kind === 'not_live';
  const isNotReady = kind === 'load_error';
  const isComingSoon = isNotLive || isNotReady;

  const pageTitle = isComingSoon ? 'Coming Soon' : 'Not Found';

  const Icon = isComingSoon ? CalendarCheck : MagnifyingGlass;

  if (isNotReady) {
    return (
      <main className="flex min-h-screen-safe items-center justify-center px-5 py-12">
        <DocumentHead
          title="Atrivial: Quiz unavailable"
          description="This quiz could not be loaded right now."
        />
        <ActionableErrorState
          title="We couldn’t load this quiz"
          message="The quiz may still be available. Reload the page to try again, or choose another place to continue."
          actions={[
            {
              label: 'Reload page',
              onSelect: reloadPage ?? (() => window.location.reload()),
              emphasis: 'primary',
            },
            { label: 'Go home', to: routes.home },
            { label: 'Contact support', to: routes.contactUs },
          ]}
        />
      </main>
    );
  }

  return (
    <div className="flex min-h-portal-loading flex-col items-center justify-center px-5 py-12 text-center">
      <DocumentHead
        title={`Atrivial: ${pageTitle}`}
        description={
          isComingSoon
            ? 'This quiz is not available yet.'
            : `The ${title} quiz is currently unavailable.`
        }
      />

      <div className="bg-bg-raised flex w-full max-w-lg flex-col items-center gap-6 rounded-2xl border border-outline-default/50 p-8 shadow-sm md:p-12">
        <div className="bg-bg-subtle flex h-16 w-16 items-center justify-center rounded-full text-accent-primary">
          <Icon size={32} weight="duotone" />
        </div>

        <div className="flex flex-col gap-2">
          <h1 className="font-title text-display leading-tight tracking-tight">
            {isNotReady ? 'Coming soon' : isNotLive ? 'Coming Soon!' : 'Quiz Not Found'}
          </h1>

          <p className="mx-auto max-w-xs text-body text-glyph-secondary">
            {isNotReady
              ? "This quiz isn't live yet."
              : isNotLive
                ? `The ${title} quiz is scheduled to go live soon.`
                : "We couldn't find the quiz you're looking for. It may have been moved or deleted."}
          </p>
        </div>

        {isNotLive && scheduledAt && (
          <div className="bg-bg-subtle rounded-xl border border-outline-default/30 px-4 py-3">
            <span className="mb-1 block text-small font-bold tracking-wider text-glyph-secondary uppercase">
              Live on
            </span>
            <span className="text-body font-bold text-glyph-primary">
              {formatScheduledTime(scheduledAt)}
            </span>
          </div>
        )}

        <div className="mt-4 w-full">
          <Button to={routes.play} variant="cta" className="w-full">
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
